import { z } from "zod";

/**
 * Birdie v4's question to the platform: what is this signed-in user to Birdie?
 * (docs/platform/birdie-audience.md)
 *
 * Birdie, Birdwatch's agent server, verifies the caller's Clerk token itself,
 * but roles live only in the platform database, so it forwards the caller's
 * own bearer token to the platform. The audience rule lives in the platform
 * (`platform/src/resources/birdie/_calc/birdie-audience.ts`); this one schema
 * serves both sides — the platform's handler satisfies it, Birdie parses with
 * it — so neither can change the shape alone.
 */

/**
 * Where Birdie asks, relative to the platform's base URL: a GET with the
 * caller's own bearer token and no body. The answer is
 * `{ data: BirdieAudienceResult }`.
 */
export const BIRDIE_AUDIENCE_PATH = "/api/v5/birdie/audience";

/**
 * What a signed-in user is to Birdie: `staff` (the people who answer
 * customers), `member` (a Birdwatch customer), or `none` (no Birdie channel
 * admits them).
 */
const BIRDIE_AUDIENCES = ["staff", "member", "none"] as const;
export type BirdieAudience = (typeof BIRDIE_AUDIENCES)[number];

export const birdieAudienceResultSchema = z.object({
  /** The platform `User.id` the token resolved to. */
  userId: z.string().min(1),
  /** The user's role keys, as the platform resolved them. */
  roles: z.array(z.string()),
  audience: z.enum(BIRDIE_AUDIENCES),
  /**
   * Who is acting, when the request carries a full-delegation actor token (an admin's impersonation, minted by
   * `POST /auth/resolve-user-token`): the platform user who minted it. Null for the user's own sign-in. A scoped token
   * never gets an answer here: the route is on no scope's list.
   */
  actor: z
    .object({
      userId: z.string().min(1),
      email: z.string().nullable(),
      name: z.string().nullable(),
    })
    .nullable()
    .default(null),
});
export type BirdieAudienceResult = z.infer<typeof birdieAudienceResultSchema>;
